/**
 * @file wq-cart-drawer.js
 * @description Slide-out cart drawer with full CRUD operations.
 *              Extends window.WQ.Cart with drawer management.
 * @sprint Phase 3 | Step 9
 */
window.WQ = window.WQ || {};

window.WQ.CartDrawer = (() => {
  'use strict';

  /* ── DOM References ── */
  const drawer = document.getElementById('wq-cart-drawer');
  const overlay = document.getElementById('wq-cart-overlay');
  const closeBtn = document.getElementById('wq-cart-drawer-close');
  const itemsContainer = document.getElementById('wq-cart-drawer-items');
  const footerEl = document.getElementById('wq-cart-drawer-footer');
  const emptyEl = document.getElementById('wq-cart-drawer-empty');
  const countEl = document.getElementById('wq-cart-drawer-count');
  const subtotalEl = document.getElementById('wq-cart-drawer-subtotal');

  if (!drawer) return {};

  let cartData = null;
  let isUpdating = false;

  /* ── Public API ── */

  function open() {
    drawer.classList.add('wq-cart-drawer--open');
    overlay.classList.add('wq-cart-overlay--open');
    drawer.setAttribute('aria-hidden', 'false');
    overlay.setAttribute('aria-hidden', 'false');
    document.body.classList.add('wq-cart-open');
    refresh();
    window.dispatchEvent(new CustomEvent('cart:opened'));
  }

  function close() {
    drawer.classList.remove('wq-cart-drawer--open');
    overlay.classList.remove('wq-cart-overlay--open');
    drawer.setAttribute('aria-hidden', 'true');
    overlay.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('wq-cart-open');
    window.dispatchEvent(new CustomEvent('cart:closed'));
  }

  async function refresh() {
    try {
      cartData = await getCart();
      renderItems(cartData);
      updateBadge(cartData.item_count);
      window.dispatchEvent(new CustomEvent('cart:updated', { detail: cartData }));
    } catch (err) {
      console.error('[WQ Cart Drawer] Refresh failed:', err);
    }
  }

  /* ── Cart API ── */

  async function getCart() {
    const res = await fetch('/cart.js', {
      headers: { 'Accept': 'application/json' }
    });
    if (!res.ok) throw new Error('Failed to fetch cart');
    return res.json();
  }

  async function addItem(variantId, quantity, properties) {
    quantity = quantity || 1;
    properties = properties || {};
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: quantity, properties: properties })
    });
    if (!res.ok) throw new Error('Cart add error: ' + res.status);
    const data = await res.json();
    await refresh();
    open();
    return data;
  }

  async function addMultipleItems(items) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: items })
    });
    if (!res.ok) throw new Error('Cart add error: ' + res.status);
    const data = await res.json();
    await refresh();
    open();
    return data;
  }

  async function updateItem(lineKey, quantity) {
    if (isUpdating) return;
    isUpdating = true;
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity: quantity })
      });
      if (!res.ok) throw new Error('Cart update error: ' + res.status);
      await refresh();
    } finally {
      isUpdating = false;
    }
  }

  async function removeItem(lineKey) {
    return updateItem(lineKey, 0);
  }

  /* ── Render cart items ── */

  function renderItems(cart) {
    if (!cart || cart.item_count === 0) {
      itemsContainer.innerHTML = '';
      footerEl.hidden = true;
      emptyEl.hidden = false;
      return;
    }

    footerEl.hidden = false;
    emptyEl.hidden = true;

    var html = '';
    cart.items.forEach(function(item) {
      var imgSrc = item.featured_image && item.featured_image.url
        ? item.featured_image.url.replace(/(\.\w+)$/, '_120x120$1')
        : '';
      var sku = item.sku || '';
      var linePrice = formatMoney(item.final_line_price);

      html += '<div class="wq-cart-drawer__item" data-key="' + esc(item.key) + '">';

      /* Thumbnail */
      html += '<div class="wq-cart-drawer__item-image">';
      if (imgSrc) {
        html += '<img src="' + esc(imgSrc) + '" alt="' + esc(item.title) + '" loading="lazy">';
      }
      html += '</div>';

      /* Info */
      html += '<div class="wq-cart-drawer__item-info">';
      html += '<p class="wq-cart-drawer__item-title">' + esc(item.product_title) + '</p>';
      if (sku) {
        html += '<p class="wq-cart-drawer__item-sku">SKU: ' + esc(sku) + '</p>';
      }

      /* Controls row: qty + price */
      html += '<div class="wq-cart-drawer__item-controls">';

      /* Quantity */
      html += '<div class="wq-cart-drawer__qty">';
      html += '<button class="wq-cart-drawer__qty-btn" data-action="decrease" data-key="' + esc(item.key) + '" data-qty="' + (item.quantity - 1) + '" aria-label="Decrease quantity">&minus;</button>';
      html += '<span class="wq-cart-drawer__qty-value">' + item.quantity + '</span>';
      html += '<button class="wq-cart-drawer__qty-btn" data-action="increase" data-key="' + esc(item.key) + '" data-qty="' + (item.quantity + 1) + '" aria-label="Increase quantity">+</button>';
      html += '</div>';

      /* Price */
      html += '<span class="wq-cart-drawer__item-price">' + linePrice + '</span>';
      html += '</div>';

      /* Remove */
      html += '<button class="wq-cart-drawer__remove" data-action="remove" data-key="' + esc(item.key) + '">Remove</button>';

      html += '</div></div>';
    });

    itemsContainer.innerHTML = html;
    subtotalEl.textContent = formatMoney(cart.total_price);
    countEl.textContent = cart.item_count;
  }

  /* ── Update badge (header icon) ── */

  function updateBadge(count) {
    /* Update drawer count */
    if (countEl) countEl.textContent = count || '';

    /* Update global header badge */
    var badge = document.getElementById('wq-cart-badge');
    if (badge) {
      badge.textContent = count || '';
      badge.hidden = !count;
    }

    /* Also update Horizon theme's cart count if present */
    var horizonBadge = document.querySelector('.cart-count-bubble span[aria-hidden]');
    if (horizonBadge) horizonBadge.textContent = count || '0';
  }

  /* ── Format money ── */

  function formatMoney(cents) {
    if (typeof cents !== 'number') return '$0.00';
    return '$' + (cents / 100).toFixed(2);
  }

  /* ── Escape HTML ── */

  function esc(str) {
    if (!str) return '';
    return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  /* ── Event Delegation ── */

  /* Close button + overlay */
  closeBtn.addEventListener('click', close);
  overlay.addEventListener('click', close);

  /* Escape key */
  document.addEventListener('keydown', function(e) {
    if (e.key === 'Escape' && drawer.classList.contains('wq-cart-drawer--open')) {
      close();
    }
  });

  /* Quantity + remove buttons */
  itemsContainer.addEventListener('click', function(e) {
    var btn = e.target.closest('[data-action]');
    if (!btn) return;

    var action = btn.getAttribute('data-action');
    var key = btn.getAttribute('data-key');

    if (action === 'remove') {
      removeItem(key);
    } else if (action === 'decrease' || action === 'increase') {
      var qty = parseInt(btn.getAttribute('data-qty'), 10);
      if (qty < 0) qty = 0;
      updateItem(key, qty);
    }
  });

  /* ── Intercept existing WQ.Cart calls ── */
  /* Override WQ.Cart to also open drawer on add */
  if (window.WQ && window.WQ.Cart) {
    var origAddItem = window.WQ.Cart.addItem;
    var origAddMultiple = window.WQ.Cart.addMultipleItems;

    window.WQ.Cart.addItem = async function(variantId, quantity, properties) {
      var result = await origAddItem(variantId, quantity, properties);
      await refresh();
      open();
      return result;
    };

    window.WQ.Cart.addMultipleItems = async function(items) {
      var result = await origAddMultiple(items);
      await refresh();
      open();
      return result;
    };
  }

  /* ── Listen for cart-trigger clicks (generic) ── */
  document.addEventListener('click', function(e) {
    var trigger = e.target.closest('[data-cart-open]');
    if (trigger) {
      e.preventDefault();
      open();
    }
  });

  /* ── Initial badge on page load ── */
  getCart().then(function(cart) {
    updateBadge(cart.item_count);
  }).catch(function() { /* silent */ });

  /* ── Public interface ── */
  return {
    open: open,
    close: close,
    refresh: refresh,
    getCart: getCart,
    addItem: addItem,
    addMultipleItems: addMultipleItems,
    updateItem: updateItem,
    removeItem: removeItem,
    updateBadge: updateBadge
  };
})();
